import { useState } from 'react';
import EmailPassword from './components/EmailPassword';
import Multifactor from './components/Multifactor';
import NotificationContainer from './components/NotificationContainer';
import SingleSignOn from './components/SingleSignOn';
import { AuthMethod, LoginMethod, MultifactorSignInViewModel, SignInModel } from './components/UserModels';

interface Props {
  signInModel?: SignInModel;
  multifactorSignInModel?: MultifactorSignInViewModel;
}

const App = ({ signInModel, multifactorSignInModel }: Props) => {
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [multifactorRequestModel, setMultifactorRequestModel] = useState(multifactorSignInModel);

  const headerMap = new Map<AuthMethod, string>([
    [AuthMethod.EMAILPASSWORD, `Sign in to ${signInModel?.PaymentCategoryModel?.Name}`],
    [AuthMethod.SSO, `Sign in to ${signInModel?.PaymentCategoryModel?.Name} with SSO`],
    [AuthMethod.MFA, 'Select Authentication Method'],
  ]);

  const determineAuthMethod = () => {
    if (signInModel?.LoginMethod !== null && signInModel?.LoginMethod === LoginMethod.SingleSignOn) {
      return AuthMethod.SSO;
    } else if (multifactorRequestModel?.AuthAppEnabled) {
      return AuthMethod.MFA;
    } else {
      return AuthMethod.EMAILPASSWORD;
    }
  };

  const [currentAuthMethod, setCurrentAuthMethod] = useState<AuthMethod>(determineAuthMethod());
  const showInvalidReasonMessage =
    new URLSearchParams(globalThis.location.search).get('reason')?.toLowerCase() === 'invalid';
  const signOutMessage = showInvalidReasonMessage ? 'Logged out due to inactivity.' : signInModel?.InactivityMessage;

  const handleRequest = (authMethod: AuthMethod, model: MultifactorSignInViewModel = {}) => {
    setCurrentAuthMethod(authMethod);
    setMultifactorRequestModel(model);
  };

  return (
    <div className="login-wrapper">
      <div className="box" style={currentAuthMethod === AuthMethod.MFA ? { width: '800px' } : {}}>
        <div className="content-wrap">
          <h6>{headerMap.get(currentAuthMethod)}</h6>
          <NotificationContainer
            confirmationMessage={confirmationMessage}
            setConfirmationMessage={setConfirmationMessage}
          />
          {signOutMessage && currentAuthMethod !== AuthMethod.MFA && (
            <div className="sign-out-message">{signOutMessage}</div>
          )}
          {currentAuthMethod === AuthMethod.EMAILPASSWORD && (
            <EmailPassword
              onRequestMultifactor={(model) => {
                handleRequest(AuthMethod.MFA, model);
              }}
              setConfirmationMessage={setConfirmationMessage}
              onRequestSSO={() => handleRequest(AuthMethod.SSO)}
              lastLoginEmailAddress={signInModel?.EmailAddress}
              initialErrorMessage={signInModel?.ErrorMessage}
            />
          )}
          {currentAuthMethod === AuthMethod.SSO && (
            <SingleSignOn
              onRequestEmailPassword={() => handleRequest(AuthMethod.EMAILPASSWORD)}
              lastLoginEmailAddress={signInModel?.EmailAddress}
            />
          )}
          {currentAuthMethod === AuthMethod.MFA && (
            <>
              <p>A second step is required to sign in. Select one of the following methods to complete login.</p>
              <Multifactor
                authAppEnabled={multifactorRequestModel?.AuthAppEnabled}
                maskedPhoneNumber={multifactorRequestModel?.PhoneNumber}
                setConfirmationMessage={setConfirmationMessage}
                onRequestEmailPassword={() => handleRequest(AuthMethod.EMAILPASSWORD)}
              />
            </>
          )}
        </div>
      </div>
      <div className="no-account">
        <p>Don&apos;t have an account?</p>
        <a href="http://www.epaypolicy.com">Learn more about ePayPolicy.</a>
      </div>
    </div>
  );
};

export default App;
